<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'create',
    subject: 'Expense',
  },
})

const { useApi } = await import('@/utils/api')
const router = useRouter()

// ── State ────────────────────────────────────────────────────────────────────
const categories = ref([])
const saving = ref(false)
const errors = ref({})

const form = ref({
  expense_category_id: null,
  amount: '',
  description: '',
  expense_date: new Date().toISOString().slice(0, 10),
  is_recurring: false,
})

const receiptFile = ref(null)
const receiptPreview = ref(null)

// ── Fetch categories ─────────────────────────────────────────────────────────
const fetchCategories = async () => {
  try {
    const response = await useApi('/api/v1/expense-categories', {
      query: { active_only: 1 },
    }).get()
    const data = response?.data ?? response
    categories.value = Array.isArray(data) ? data : (data?.data ?? [])
  } catch (e) {
    console.error(e)
  }
}

onMounted(fetchCategories)

// ── Receipt preview ──────────────────────────────────────────────────────────
const onReceiptChange = (file) => {
  if (!file) {
    receiptPreview.value = null
    receiptFile.value = null
    return
  }
  receiptFile.value = file
  const reader = new FileReader()
  reader.onload = (e) => { receiptPreview.value = e.target.result }
  reader.readAsDataURL(file)
}

// ── Submit ───────────────────────────────────────────────────────────────────
const submit = async () => {
  saving.value = true
  errors.value = {}

  try {
    // Pakai FormData supaya bisa upload file sekaligus
    const fd = new FormData()
    fd.append('expense_category_id', form.value.expense_category_id)
    fd.append('amount', form.value.amount)
    fd.append('description', form.value.description)
    fd.append('expense_date', form.value.expense_date)
    fd.append('is_recurring', form.value.is_recurring ? '1' : '0')
    if (receiptFile.value) {
      fd.append('receipt_image', receiptFile.value)
    }

    const accessToken = useCookie('accessToken').value
    const tenantSlug = useCookie('current_tenant').value

    const headers = {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    }
    if (tenantSlug) headers['X-Tenant'] = tenantSlug
    // ⚠️ Jangan set Content-Type — browser akan set boundary multipart otomatis

    const { $api } = await import('@/utils/api')
    await $api('/api/v1/expenses', {
      method: 'POST',
      headers,
      body: fd,
    })

    router.push({ name: 'expense' })
  } catch (e) {
    if (e?.response?.status === 422) {
      errors.value = e?.data?.errors ?? {}
    } else {
      console.error(e)
    }
  } finally {
    saving.value = false
  }
}

// ── Format ───────────────────────────────────────────────────────────────────
const formatAmount = computed({
  get: () => form.value.amount ? Number(form.value.amount).toLocaleString('id-ID') : '',
  set: (val) => {
    form.value.amount = val.replace(/\D/g, '')
  },
})
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VBreadcrumbs
      :items="[
        { title: 'Pengeluaran', to: { name: 'expense' } },
        { title: 'Tambah Pengeluaran', disabled: true },
      ]"
      class="px-0 mb-4"
    />

    <VRow>
      <!-- Form -->
      <VCol cols="12" md="7" lg="8">
        <VCard>
          <VCardTitle class="pa-6 pb-0">Detail Pengeluaran</VCardTitle>
          <VCardText class="pa-6">
            <VRow>
              <!-- Kategori -->
              <VCol cols="12">
                <VSelect
                  v-model="form.expense_category_id"
                  :items="categories"
                  item-title="name"
                  item-value="id"
                  label="Kategori Pengeluaran *"
                  placeholder="Pilih kategori"
                  :error-messages="errors.expense_category_id"
                />
              </VCol>

              <!-- Deskripsi -->
              <VCol cols="12">
                <VTextField
                  v-model="form.description"
                  label="Deskripsi *"
                  placeholder="cth. Pembelian biji kopi arabica"
                  :error-messages="errors.description"
                />
              </VCol>

              <!-- Jumlah -->
              <VCol cols="12" sm="6">
                <VTextField
                  v-model="formatAmount"
                  label="Jumlah (Rp) *"
                  placeholder="0"
                  prepend-inner-icon="ri-money-rupee-circle-line"
                  :error-messages="errors.amount"
                  inputmode="numeric"
                />
              </VCol>

              <!-- Tanggal -->
              <VCol cols="12" sm="6">
                <VTextField
                  v-model="form.expense_date"
                  label="Tanggal Pengeluaran *"
                  type="date"
                  :error-messages="errors.expense_date"
                />
              </VCol>

              <!-- Berulang -->
              <VCol cols="12">
                <VSwitch
                  v-model="form.is_recurring"
                  label="Pengeluaran Berulang (bulanan)"
                  color="primary"
                  hide-details
                />
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Struk & Actions -->
      <VCol cols="12" md="5" lg="4">
        <!-- Upload struk -->
        <VCard class="mb-4">
          <VCardTitle class="pa-6 pb-0">Foto Struk</VCardTitle>
          <VCardText class="pa-6">
            <!-- Preview -->
            <div
              v-if="receiptPreview"
              class="mb-3"
            >
              <VImg
                :src="receiptPreview"
                max-height="200"
                rounded
                cover
              />
              <VBtn
                variant="tonal"
                color="error"
                size="small"
                class="mt-2"
                prepend-icon="ri-delete-bin-line"
                @click="onReceiptChange(null)"
              >
                Hapus Foto
              </VBtn>
            </div>

            <!-- Upload area -->
            <VFileInput
              v-else
              label="Upload Struk (opsional)"
              accept="image/*"
              prepend-icon="ri-camera-line"
              :error-messages="errors.receipt_image"
              hint="Maks. 2MB. Format: JPG, PNG, WEBP"
              persistent-hint
              @update:model-value="onReceiptChange"
            />
          </VCardText>
        </VCard>

        <!-- Actions -->
        <VCard>
          <VCardText class="pa-6">
            <VBtn
              color="primary"
              block
              size="large"
              :loading="saving"
              prepend-icon="ri-save-line"
              @click="submit"
            >
              Simpan Pengeluaran
            </VBtn>
            <VBtn
              variant="tonal"
              color="secondary"
              block
              class="mt-3"
              :disabled="saving"
              :to="{ name: 'expense' }"
            >
              Batal
            </VBtn>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>
